import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import ListingDetailModal from "./ListingDetailModal";
import { ANALYTICS_FONT_FAMILY } from "./analyticsTypography";
import { SHARE_EVENT_KEYS } from "./shareEvents";

// ── CSV export helpers ─────────────────────────────────────────────────────
const csvEscapeL = (v) => {
  const s = String(v ?? '');
  return s.includes(',') || s.includes('"') || s.includes('\n')
    ? `"${s.replace(/"/g, '""')}"`
    : s;
};
const downloadListingsCSV = (realListings) => {
  const headers = ['Ref', 'Uploaded At', 'Gallery URL', 'Views', 'Shuffles', 'Shares', 'Saves'];
  const rows = realListings.map((l) => [
    l.ref || '',
    l.uploadedAt || '',
    l.galleryUrl || '',
    l.stats?.webviewOpens ?? 0,
    l.stats?.shuffles ?? 0,
    l.stats?.shares ?? 0,
    l.stats?.saves ?? 0,
  ]);
  const csv = [headers, ...rows].map((r) => r.map(csvEscapeL).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'listings.csv';
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Listings tab.
 *
 * Mobile-first read-only list of uploads keyed by customer_ref. Everything
 * here is derived locally from the same `/timeseries` payload the Feed tab
 * fetches — no extra API calls, no extra polling.
 *
 * Fields whose source data is not yet wired (preview image, listing
 * status, days on market) render as honest placeholders with a "Coming
 * soon" affordance. The two filter rows above the list lean into that:
 * status filtering is intentionally disabled until the listing platform
 * is connected, while date-uploaded sort is fully functional today.
 *
 * Cheap to render:
 *   - one useMemo derives the listings array from rows; recomputes only
 *     when rows or sort direction change.
 *   - card content is plain divs + small SVG glyphs; no charts, no
 *     network image fetches, no animations beyond color transitions.
 *   - "Load more" reuses the same paginator the Feed tab already uses.
 */

const STATUS_FILTERS = [
  { value: "all", label: "All Listings" },
  { value: "on_market", label: "On Market" },
  { value: "pending", label: "Pending" },
  { value: "sold", label: "Sold" },
  { value: "off_market", label: "Off Market" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Sort by: Newest first" },
  { value: "oldest", label: "Sort by: Oldest first" },
];

function formatRelative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const diff = Math.max(0, Date.now() - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Group raw events into per-listing buckets keyed by customer_ref.
 *
 * Mirrors the FeedList grouper but extracts richer per-bucket stats so
 * the ListingDetailModal can render without touching the network.
 */
/**
 * Placeholder shown when no real (ref-tagged) listings exist yet.
 * Marked with `isSample: true` so the card can display a clear indicator.
 */
const SAMPLE_LISTING = {
  key: "__sample__",
  ref: "SAMPLE-REF-001",
  uploadedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  layoutId: "sample-layout-id",
  galleryUrl: null,
  origin: "demo",
  isSample: true,
  stats: { total: 12, sessions: 2, shuffles: 4, saves: 1, shares: 1, webviewOpens: 3 },
};

/**
 * Build listings from feed rows, but ONLY for entries that have a real
 * customer_ref (i.e. uploaded from within a host app such as S.MPLE).
 * Direct uploads without a ref belong to the Feed tab, not Listings.
 */
function buildListingsFromRows(rows) {
  const buckets = new Map();

  rows.forEach((row) => {
    const rawRef = row?.meta?.customer_ref;
    const hasRealRef =
      rawRef &&
      !String(rawRef).includes("not-provided") &&
      !String(rawRef).startsWith("http");
    // Skip sessions without a real ref — they are direct uploads, not listings
    if (!hasRealRef) return;
    const key = `ref_${rawRef}`;

    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        key,
        ref: rawRef,
        rows: [],
        userIds: new Set(),
      };
      buckets.set(key, bucket);
    }
    bucket.rows.push(row);
    if (row?.meta?.user) bucket.userIds.add(row.meta.user);
  });

  const listings = [];
  buckets.forEach((bucket) => {
    const events = bucket.rows;
    const uploadEvent = events.find(
      (r) => r?.meta?.event === "floorplan_uploaded"
    );
    const galleryReadyEvent = events.find(
      (r) => r?.meta?.event === "gallery_ready"
    );

    const counts = events.reduce((acc, r) => {
      const e = r?.meta?.event;
      if (!e) return acc;
      acc[e] = (acc[e] || 0) + 1;
      return acc;
    }, {});

    const uploadedAt =
      uploadEvent?.timestamp ||
      events.reduce((earliest, r) => {
        if (!r?.timestamp) return earliest;
        if (!earliest) return r.timestamp;
        return new Date(r.timestamp) < new Date(earliest)
          ? r.timestamp
          : earliest;
      }, null);

    listings.push({
      key: bucket.key,
      ref: bucket.ref,
      uploadedAt,
      layoutId: galleryReadyEvent?.detail?.uniqueId || galleryReadyEvent?.detail?.galleryId || null,
      galleryUrl: galleryReadyEvent?.detail?.galleryUrl || null,
      origin: events[0]?.meta?.origin || null,
      stats: {
        total: events.length,
        sessions: bucket.userIds.size,
        shuffles: counts.shuffle_room || 0,
        saves: counts.layout_saved || 0,
        shares: SHARE_EVENT_KEYS.reduce((n, k) => n + (counts[k] || 0), 0),
        webviewOpens: counts.webview_opened || 0,
      },
    });
  });

  return listings;
}

const FloorPlanThumbGlyph = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 28 28"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="3"
      width="22"
      height="22"
      rx="3"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M3 11h22M14 3v22"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const THUMB_SIZE = 72;

const ListingCardSkeleton = () => (
  <Box
    sx={{
      px: { xs: 1.75, sm: 2 },
      py: 1.75,
      borderTop: "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    <Skeleton
      variant="rounded"
      width={THUMB_SIZE}
      height={THUMB_SIZE}
      sx={{ borderRadius: "10px", flexShrink: 0 }}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Skeleton variant="text" width="55%" />
      <Skeleton variant="text" width="35%" />
    </Box>
    <Skeleton variant="circular" width={28} height={28} />
  </Box>
);

const StatusFilterBar = ({ value, onChange }) => (
  <Select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    size="small"
    fullWidth
    sx={{
      bgcolor: "#fff",
      borderRadius: "10px",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0,0,0,0.1)",
      },
      "& .MuiSelect-select": {
        fontSize: "0.85rem",
        py: 1,
      },
    }}
  >
    {STATUS_FILTERS.map((opt) => (
      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.85rem" }}>
        {opt.label}
      </MenuItem>
    ))}
  </Select>
);

const SortBar = ({ value, onChange }) => (
  <Select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    size="small"
    fullWidth
    sx={{
      bgcolor: "#fff",
      borderRadius: "10px",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(0,0,0,0.1)",
      },
      "& .MuiSelect-select": {
        fontSize: "0.85rem",
        py: 1,
      },
    }}
  >
    {SORT_OPTIONS.map((opt) => (
      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.85rem" }}>
        {opt.label}
      </MenuItem>
    ))}
  </Select>
);

const ListingCard = ({ listing, onOpen }) => {
  const refLabel = listing.ref || "Direct upload";
  const stats = listing.stats || {};
  const compactPills = [];
  if (stats.shuffles)
    compactPills.push({ key: "shuffles", label: `${stats.shuffles} shuffles` });
  if (stats.saves)
    compactPills.push({ key: "saves", label: `${stats.saves} saves` });
  if (stats.shares) compactPills.push({ key: "shares", label: "shared" });

  return (
    <Box
      sx={{
        position: "relative",
        borderTop: "1px solid rgba(0,0,0,0.06)",
        "&:first-of-type": { borderTop: "none" },
      }}
    >
      <ButtonBase
        onClick={onOpen}
        sx={{
          width: "100%",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 1.75, sm: 2 },
          py: 1.75,
          pr: 5.5,
          minHeight: 88,
          transition: "background 120ms ease",
          // Defeats global `button { border-radius: 50px !important; text-transform: capitalize !important }`
          borderRadius: "0 !important",
          textTransform: "none !important",
          "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
          "&:active": { bgcolor: "rgba(0,0,0,0.04)" },
        }}
      >
        <Box
          sx={{
            width: THUMB_SIZE,
            height: THUMB_SIZE,
            borderRadius: "10px",
            bgcolor: listing.isSample ? "rgba(99,102,241,0.06)" : "rgba(0,0,0,0.04)",
            border: listing.isSample ? "1px dashed rgba(99,102,241,0.3)" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: listing.isSample ? "rgba(99,102,241,0.5)" : "rgba(0,0,0,0.35)",
            flexShrink: 0,
          }}
        >
          <FloorPlanThumbGlyph />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: "rgba(0,0,0,0.9)",
                fontFamily: ANALYTICS_FONT_FAMILY,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {refLabel}
            </Typography>
            {listing.isSample && (
              <Box
                component="span"
                sx={{
                  px: 0.75,
                  py: 0.125,
                  bgcolor: "rgba(99,102,241,0.1)",
                  color: "rgba(99,102,241,0.85)",
                  borderRadius: "4px",
                  fontSize: "0.6rem",
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  flexShrink: 0,
                  lineHeight: 1.6,
                }}
              >
                SAMPLE
              </Box>
            )}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 0.25 }}
          >
            <Typography
              variant="caption"
              sx={{ color: "rgba(0,0,0,0.5)", fontSize: "0.75rem" }}
              noWrap
            >
              {formatRelative(listing.uploadedAt)}
            </Typography>
            <Box
              component="span"
              sx={{
                width: 3,
                height: 3,
                bgcolor: "rgba(0,0,0,0.25)",
                borderRadius: "50%",
                flexShrink: 0,
              }}
            />
            <Box
              component="span"
              sx={{
                px: 0.75,
                py: 0.125,
                bgcolor: "rgba(0,0,0,0.04)",
                borderRadius: "4px",
                fontSize: "0.65rem",
                fontWeight: 500,
                color: "rgba(0,0,0,0.5)",
                lineHeight: 1.6,
                flexShrink: 0,
              }}
            >
              Status TBD
            </Box>
          </Stack>

          {compactPills.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.5 }}
            >
              {compactPills.map((p) => (
                <Box
                  key={p.key}
                  component="span"
                  sx={{
                    px: 0.75,
                    py: 0.125,
                    bgcolor: "rgba(0,0,0,0.04)",
                    borderRadius: "4px",
                    fontSize: "0.65rem",
                    fontWeight: 500,
                    color: "rgba(0,0,0,0.65)",
                    lineHeight: 1.6,
                  }}
                >
                  {p.label}
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>
      </ButtonBase>

      <IconButton
        size="small"
        aria-label="Listing details"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        sx={{
          position: "absolute",
          top: "50%",
          right: 8,
          transform: "translateY(-50%)",
          color: "rgba(0,0,0,0.45)",
          "&:hover": { color: "rgba(0,0,0,0.75)" },
        }}
      >
        <MoreHorizIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Box>
  );
};

const ListingsView = ({
  rows,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  subtitle,
  /** When true, no sample listing — show partner empty state instead. */
  partnerEmptyMode = false,
}) => {
  const [sort, setSort] = useState("newest");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeListing, setActiveListing] = useState(null);
  const [noNewListings, setNoNewListings] = useState(false);
  const preLoadCountRef = useRef(null);

  const listings = useMemo(() => {
    const built = Array.isArray(rows) ? buildListingsFromRows(rows) : [];
    built.sort((a, b) => {
      const ta = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
      const tb = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
      return sort === "oldest" ? ta - tb : tb - ta;
    });
    if (built.length === 0 && !loading && !partnerEmptyMode) return [SAMPLE_LISTING];
    return built;
  }, [rows, sort, loading, partnerEmptyMode]);

  // Detect when a "load more" completes without adding new listings (#13)
  useEffect(() => {
    if (!loadingMore && preLoadCountRef.current !== null) {
      const realCount = listings.filter((l) => !l.isSample).length;
      setNoNewListings(realCount === preLoadCountRef.current);
      preLoadCountRef.current = null;
    }
  }, [loadingMore, listings]);

  const handleLoadMore = () => {
    preLoadCountRef.current = listings.filter((l) => !l.isSample).length;
    setNoNewListings(false);
    onLoadMore();
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ mb: 1.5 }}
        alignItems="center"
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StatusFilterBar value={statusFilter} onChange={setStatusFilter} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SortBar value={sort} onChange={setSort} />
        </Box>
        {listings.some((l) => !l.isSample) && (
          <Box
            component="button"
            type="button"
            onClick={() =>
              downloadListingsCSV(listings.filter((l) => !l.isSample))
            }
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'rgba(0,0,0,0.35)',
              fontSize: '0.72rem',
              fontWeight: 500,
              fontFamily: 'inherit',
              p: 0,
              flexShrink: 0,
              lineHeight: 1,
              '&:hover': { color: 'rgba(0,0,0,0.65)' },
            }}
          >
            <FileDownloadOutlined sx={{ fontSize: 13 }} />
            Export
          </Box>
        )}
      </Stack>

      {listings.length === 1 && listings[0].isSample && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 1,
            px: 0.5,
            color: "rgba(0,0,0,0.5)",
            fontSize: "0.75rem",
          }}
        >
          No listings linked yet. Listings will appear when uploads arrive from a host app with an attached ref.
        </Typography>
      )}

      {partnerEmptyMode && listings.length === 0 && !loading && (
        <Box
          sx={{
            borderRadius: '14px',
            border: '1px solid rgba(0,0,0,0.06)',
            bgcolor: '#fff',
            px: { xs: 1.75, sm: 2 },
            py: { xs: 2, sm: 2.5 },
          }}
        >
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'rgba(0,0,0,0.65)', lineHeight: 1.5 }}>
            No listings linked yet for this period.
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.45)', mt: 0.75, lineHeight: 1.5 }}>
            Per-listing analytics appear when the original staged floor plan link is tied to a
            listing ID (for example from Luxury Presence). Agent uploads and client copies stay
            separate cards in Recent Activity until listing lineage groups them. Listing links
            will be manageable from MagicFurnish settings in a future update.
          </Typography>
        </Box>
      )}

      {(listings.length > 0 || (loading && !partnerEmptyMode)) && (
      <Box
        sx={{
          borderRadius: "14px",
          border: listings.length === 1 && listings[0].isSample
            ? "1px dashed rgba(99,102,241,0.2)"
            : "1px solid rgba(0,0,0,0.06)",
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
        {loading && listings.every((l) => l.isSample) ? (
          <>
            <ListingCardSkeleton />
            <ListingCardSkeleton />
            <ListingCardSkeleton />
          </>
        ) : (
          listings.map((listing) => (
            <ListingCard
              key={listing.key}
              listing={listing}
              onOpen={() => setActiveListing(listing)}
            />
          ))
        )}
      </Box>
      )}

      {noNewListings && (
        <Typography
          variant="caption"
          sx={{ display: "block", textAlign: "center", mt: 1.5, color: "rgba(0,0,0,0.35)", fontSize: "0.72rem" }}
        >
          No new listings in this batch — events loaded but none had a ref attached.
        </Typography>
      )}

      {hasMore && listings.length > 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loadingMore}
            startIcon={
              loadingMore ? <CircularProgress size={14} thickness={5} /> : null
            }
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.85rem",
              borderColor: "rgba(0,0,0,0.1)",
              color: "rgba(0,0,0,0.75)",
              "&:hover": {
                borderColor: "rgba(0,0,0,0.2)",
                bgcolor: "rgba(0,0,0,0.02)",
              },
            }}
          >
            Load more
          </Button>
        </Box>
      ) : null}

      <ListingDetailModal
        open={Boolean(activeListing)}
        onClose={() => setActiveListing(null)}
        listing={activeListing}
        subtitle={subtitle}
      />
    </Box>
  );
};

export default ListingsView;
